import { FeedbackListItem, FeedbackStatus } from '@property/shared';
import { formatTimestamp } from '@/lib/date';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useUpdateFeedbackStatus } from '@/hooks/use-feedback-mutations';
import { useUserProfile } from '@/hooks/use-task-queries';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';

const TYPE_LABELS: Record<string, string> = {
  general: 'General',
  inspection_report: 'Inspection',
  contact_query: 'Contact Query',
};

function MarkStatusButton({ item }: { item: FeedbackListItem }) {
  const { mutate, isPending } = useUpdateFeedbackStatus();
  const isUnread = item.status === FeedbackStatus.UNREAD;
  return (
    <Button
      variant="default"
      disabled={isPending}
      onClick={() =>
        mutate({ id: item.id, status: isUnread ? FeedbackStatus.READ : FeedbackStatus.UNREAD })
      }
    >
      {isPending ? 'Updating…' : isUnread ? 'Mark as Read' : 'Mark as Unread'}
    </Button>
  );
}

function FeedbackBody({ item }: { item: FeedbackListItem }) {
  const { name, userId, email, phone, type, status, createdAt, rating, message } = item;
  const shouldFetch = !name && !!userId;
  const { data: userData } = useUserProfile(shouldFetch ? userId : undefined);
  const displayName = name ?? userData?.name ?? (userId ? 'Unknown' : 'Anonymous');
  const isUnread = status === FeedbackStatus.UNREAD;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-xs">
          {TYPE_LABELS[type] ?? type}
        </Badge>
        {isUnread && <Badge className="text-xs">New</Badge>}
        <span className="text-xs text-muted-foreground ml-auto">
          {formatTimestamp(createdAt, { dateStyle: 'medium' })}
        </span>
      </div>

      <div className="space-y-0.5">
        <p className="text-sm font-semibold">{displayName}</p>
        {email && <p className="text-xs text-muted-foreground">{email}</p>}
        {phone && <p className="text-xs text-muted-foreground">{phone}</p>}
      </div>

      {rating != null && (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-4 w-4 ${rating >= star ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`}
            />
          ))}
        </div>
      )}

      {message ? (
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{message}</p>
      ) : (
        <p className="text-sm text-muted-foreground italic">No message provided.</p>
      )}
    </div>
  );
}

interface FeedbackDetailModalProps {
  item: FeedbackListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDetailModal({ item, open, onOpenChange }: FeedbackDetailModalProps) {
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const shouldFetch = !!item && !item.name && !!item.userId;
  const { data: userData } = useUserProfile(shouldFetch ? (item?.userId ?? undefined) : undefined);

  if (!item) return null;

  const title = item.name ?? userData?.name ?? (item.userId ? 'Unknown' : 'Feedback');

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <FeedbackBody item={item} />
          <DialogFooter>
            <MarkStatusButton item={item} />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <FeedbackBody item={item} />
        <div className="mt-6 pb-2 w-full">
          <MarkStatusButton item={item} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
